import {
  Dialog as ShadnDialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/UI/Dialog';
import React from 'react';

import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { cn } from '@/lib/utils';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
export type ModalVariants = 'default' | 'destructive';

export type ModalProps = {
  title?: string | React.ReactNode | React.ReactElement;
  description?: string;
  visible: boolean;
  onCancel: () => void;
  isLoading?: boolean;
  onSubmit?: () => void;
  withFooter?: boolean;
  children?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariants;
  confirmBtnText?: string;
};

export const Modal = ({
  title,
  description,
  onSubmit,
  children,
  visible,
  onCancel,
  withFooter,
  size = 'md',
  variant = 'default',
  confirmBtnText = 'Confirm',
}: ModalProps) => {
  const { t } = useTranslation();

  return (
    <ShadnDialog modal={true} open={visible} onOpenChange={onCancel}>
      <DialogContent
        className={cn(
          'max-h-[80dvh] overflow-y-auto font-rokkitt',
          size == 'sm'
            ? 'max-w-md'
            : size == 'md'
              ? 'max-w-lg'
              : size === 'lg'
                ? 'max-w-xl'
                : size == 'xl'
                  ? 'max-w-2xl'
                  : size === '2xl'
                    ? 'max-w-3xl'
                    : 'max-w-5xl'
        )}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-background-1">
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        {withFooter && (
          <DialogFooter className="flex items-center justify-end">
            <Button onClick={onCancel} variant="ghost">
              {t('global.cancel')}
            </Button>
            <Button variant={variant} onClick={onSubmit}>
              {confirmBtnText}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </ShadnDialog>
  );
};

export default Modal;
